function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import filter from 'lodash/filter';
import head from 'lodash/head';
import { decrypt, encrypt, getKeyFromPem, hybridEncrypt, hybridDecrypt } from 'helpers/crypto';
import { LocalError } from 'helpers/errorTypes';
import StorageExchangeTokenService from 'services/StorageExchangeTokenService';
import StatisticalPersonalityService from 'services/StatisticalPersonalityService';
import ApiService from 'services/ApiService';
import App from 'modules/App';
import Account from 'modules/Account';
import Information from 'modules/Information';
import CloudDrive from 'modules/CloudDrive';
import Statistics from 'modules/Statistics';
import Patient from 'modules/Patient';
import * as actions from './actions';
function* createCgProfile() {
    try {
        let cgProfile = yield select(Account.selectors.cgProfile);
        if (cgProfile) {
            yield put(actions.createCgProfileSuccess(cgProfile));
            return;
        }
        const requestURL = '/api/Account/me/CgProfile';
        cgProfile = yield call(ApiService.regionalRequest, requestURL, {
            method: 'POST',
            body: {}
        });
        yield put(actions.createCgProfileSuccess(cgProfile));
    } catch (err) {
        yield put(actions.createCgProfileError(err));
        yield call(App.dispatchError, err, Account.messages);
    }
}
function* createHcpProfile() {
    try {
        let hcpProfile = yield select(Account.selectors.hcpProfile);
        if (hcpProfile) {
            yield put(actions.createHcpProfileSuccess(hcpProfile));
            return;
        }
        const requestURL = '/api/Account/me/HcpProfile';
        hcpProfile = yield call(ApiService.regionalRequest, requestURL, {
            method: 'POST',
            body: {}
        });
        yield put(actions.createHcpProfileSuccess(hcpProfile));
    } catch (err) {
        yield put(actions.createHcpProfileError(err));
        yield call(App.dispatchError, err, Account.messages);
    }
}
function* createPatientProfile({ payload }) {
    try {
        const { diabetesType, treatmentType, weight, height, payer, authorizationCode, standards, enrollCode, invitationCode } = payload;
        let patientProfile = yield select(Account.selectors.patientProfile);
        if (patientProfile && patientProfile.encryptedExchangeToken) {
            yield put(actions.createPatientProfileSuccess(patientProfile));
        }
        const { holderUID, holderPassphrase } = yield select(Account.selectors.account);
        const passphrase = yield select(Account.selectors.passphrase);
        const keyPair = yield select(Account.selectors.keyPair);
        const pubKeyObj = yield call(getKeyFromPem, keyPair.pubKeyPem);
        const prvKeyObj = yield call(getKeyFromPem, keyPair.prvKeyPem, passphrase);
        if (!pubKeyObj) {
            const err = new LocalError({
                code: 'NoPublicKey'
            });
            yield put(actions.createPatientProfileError(err));
            yield call(App.dispatchError, err, Account.messages);
        }
        if (!patientProfile) {
            const requestURL = '/api/Account/me/PatientProfile';
            const holderEntry = {
                holderUID,
                holderPassphrase
            };
            patientProfile = yield call(ApiService.regionalRequest, requestURL, {
                method: 'POST',
                body: {
                    holderEntry,
                    payer
                }
            });
        }
        patientProfile.phiSetReferenceKey = yield call(decrypt, patientProfile.encryptedPhiSetReferenceKey, prvKeyObj);
        if (!patientProfile.countryId) {
            const countrySettings = yield select(Account.selectors.countrySettings);
            patientProfile.countryId = countrySettings.countryId;
        }
        let updateProfileData;
        let accessToken;
        if (!patientProfile.encryptedExchangeToken) {
            const tokens = yield call(StorageExchangeTokenService.fetchExchangeToken, authorizationCode, 'PatientProfile', patientProfile.patientProfileId);
            const { exchangeToken, storageProvider, storageAccount } = tokens;
            const encryptedExchangeToken = yield call(hybridEncrypt, exchangeToken, pubKeyObj);
            updateProfileData = {
                storageProvider,
                storageAccount,
                encryptedExchangeToken,
                exchangeToken
            };
            accessToken = tokens;
        } else {
            const { storageProvider, storageAccount, encryptedExchangeToken } = patientProfile;
            const exchangeToken = yield call(hybridDecrypt, encryptedExchangeToken, prvKeyObj);
            const fetchedAccessToken = yield call(StorageExchangeTokenService.fetchAccessToken, exchangeToken);
            updateProfileData = {
                storageProvider,
                storageAccount,
                encryptedExchangeToken,
                exchangeToken
            };
            accessToken = _object_spread({
                storageProvider,
                storageAccount,
                exchangeToken
            }, fetchedAccessToken);
        }
        yield put(Account.actions.updatePatientProfile(updateProfileData));
        const updatePatientProfileResult = yield take([
            Account.actionTypes.UPDATE_PATIENT_PROFILE_SUCCESS,
            Account.actionTypes.UPDATE_PATIENT_PROFILE_ERROR
        ]);
        if (updatePatientProfileResult.type === Account.actionTypes.UPDATE_PATIENT_PROFILE_ERROR) {
            yield put(actions.createPatientProfileError(updatePatientProfileResult.error));
        }
        patientProfile = _object_spread_props(_object_spread({}, patientProfile, updateProfileData), {
            accessToken,
            dataType: 'BGM'
        });
        const sourceType = App.constants.PATIENT_DATABASES.GlucoControOnline.type;
        let { statisticalPersonalityId } = patientProfile;
        if (!statisticalPersonalityId) {
            statisticalPersonalityId = yield call(StatisticalPersonalityService.resolveStatisticalPersonalityId, {
                holderUID,
                holderPassphrase
            });
            patientProfile.statisticalPersonalityId = statisticalPersonalityId;
        }
        const encryptedStatisticalPersonalityId = yield call(encrypt, statisticalPersonalityId, pubKeyObj);
        const phiSetData = {
            diabetesType,
            treatmentType,
            encryptedStatisticalPersonalityId,
            statisticalPersonalityId
        };
        let measurements;
        if (weight && height) {
            phiSetData.summaryData = {
                lastWeight: weight,
                lastHeight: height
            };
            measurements = {
                weight,
                height
            };
        }
        const information = yield select(Information.selectors.information);
        yield all([
            put(CloudDrive.actions.storeMeasurements(measurements, phiSetData, null, patientProfile)),
            put(Statistics.actions.sendStatisticalPersonalityId(statisticalPersonalityId, {
                holderUID,
                holderPassphrase
            })),
            put(Statistics.actions.sendStatistics(_object_spread_props(_object_spread({}, patientProfile, information), {
                keyPair,
                sourceType
            }), phiSetData, {}, standards, passphrase))
        ]);
        const storeMeasurementsResult = yield take([
            CloudDrive.actionTypes.STORE_MEASUREMENTS_SUCCESS,
            CloudDrive.actionTypes.STORE_MEASUREMENTS_ERROR
        ]);
        if (storeMeasurementsResult.type === CloudDrive.actionTypes.STORE_MEASUREMENTS_ERROR) {
            yield put(actions.createPatientProfileError(storeMeasurementsResult.error));
        }
        yield put(Account.actions.listWalletEntries({
            holderUID,
            holderPassphrase
        }));
        const { phiSet, phiSetDocumentId } = storeMeasurementsResult.payload;
        yield put(Patient.actions.setPhiSet(phiSet, phiSetDocumentId));
        const sharingRequests = yield call(ApiService.regionalRequest, '/api/SharingRequest/patient/me');
        const sharingRequestToBeApproved = head(filter(sharingRequests, {
            sharingStatus: 'Received'
        }));
        if (sharingRequestToBeApproved) {
            yield put(Patient.actions.approveSharingRequest(sharingRequestToBeApproved, patientProfile, phiSet));
            yield take([
                Patient.actionTypes.APPROVE_SHARING_REQUEST_SUCCESS,
                Patient.actionTypes.APPROVE_SHARING_REQUEST_ERROR
            ]);
        }
        if (enrollCode) {
            yield put(Patient.actions.enrollInClinic(enrollCode, invitationCode));
            yield take([
                Patient.actionTypes.ENROLL_IN_CLINIC_SUCCESS,
                Patient.actionTypes.ENROLL_IN_CLINIC_ERROR
            ]);
        }
        yield put(actions.createPatientProfileSuccess(patientProfile));
    } catch (err) {
        yield put(actions.createPatientProfileError(err));
        yield call(App.dispatchError, err, Account.messages);
    } finally{
        yield put(CloudDrive.actions.clearAuthorizationCode());
    }
}
function* sagas() {
    yield takeLatest(Account.actionTypes.CREATE_CG_PROFILE, createCgProfile);
    yield takeLatest(Account.actionTypes.CREATE_HCP_PROFILE, createHcpProfile);
    yield takeLatest(Account.actionTypes.CREATE_PATIENT_PROFILE, createPatientProfile);
}
export default [
    sagas
];
